import { useEffect, useState } from "react";
import { Modal } from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updateShopifyAppUser } from "../../api/auth";
import PhoneInputWithCountryCode from "../../Authentication/components/PhoneInputWithCountryCode";
import { Svg, WelcomeSteps } from "./parts";
import styles from "./welcome.module.css";

const RULES = [
  ["8+ characters", (p) => p.length >= 8],
  ["A letter and a number", (p) => /[A-Za-z]/.test(p) && /\d/.test(p)],
  ["A symbol", (p) => /[^A-Za-z0-9\s]/.test(p)],
];

const EYE = "M1.5 8s2.5-5 6.5-5 6.5 5 6.5 5-2.5 5-6.5 5S1.5 8 1.5 8zM8 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4z";
const EYE_OFF = "M2 2l12 12M6.6 6.6a2 2 0 0 0 2.8 2.8M4.2 4.3C2.6 5.4 1.5 8 1.5 8s2.5 5 6.5 5c1.3 0 2.5-.5 3.5-1.2M7 3.1C7.3 3 7.7 3 8 3c4 0 6.5 5 6.5 5s-.6 1.2-1.7 2.4";

// Step 1 of the welcome flow: the store owner picks a password and leaves a phone number.
export default function CreateAccount({ userDetails, demo }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [phone, setPhone] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [busy, setBusy] = useState(false);

  const owner = userDetails?.shop_details?.shop_owner || "there";
  const email = userDetails?.shop_details?.owner_email;

  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((r) => r.json())
      .then((d) => setIpAddress(d?.ip))
      .catch(() => {});
  }, []);

  // The button follows exactly the checks shown under the field. Any symbol
  // counts: a stricter character list kept the button grey with all checks green.
  const valid = RULES.every(([, test]) => test(password));
  // The demo walkthrough may skip the phone number; real sign ups may not.
  const ready = valid && (Boolean(phone) || demo);

  const submit = (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    if (demo) {
      navigate("/customer/dashboard?welcome=demo");
      return;
    }
    setBusy(true);
    const referralCode = JSON.parse(localStorage.getItem("referralCode"));
    dispatch(
      updateShopifyAppUser({
        token: userDetails?.token,
        password,
        first_name: userDetails?.shop_details?.shop_owner,
        last_name: "",
        phone,
        ipAddress,
        referralCode,
      })
    ).then(({ payload }) => {
      // On an error the thunk already shows a toast; stay here so they can fix it.
      if (!payload) return setBusy(false);
      localStorage.removeItem("referralCode");
      navigate("/customer/dashboard");
    });
  };

  return (
    <Modal open disableEscapeKeyDown hideBackdrop={false} BackdropProps={{ style: { backgroundColor: "rgba(23, 23, 26, 0.55)" } }}>
      <form className={`${styles.card} ${styles.modal}`} onSubmit={submit} aria-labelledby="welcome-account-title">
        <WelcomeSteps current={1} />
        <h1 id="welcome-account-title">Create your account</h1>
        <p className={styles.lead}>
          Hi {owner}, we’ll set up U-Send for {email ? <strong>{email}</strong> : "your store"}.
        </p>

        <div className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="welcome-password">Password</label>
            <div className={styles.inputWrap}>
              <input
                id="welcome-password"
                className={styles.input}
                type={show ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-describedby="welcome-password-rules"
                autoFocus
              />
              <button
                type="button"
                className={styles.eye}
                onClick={() => setShow((v) => !v)}
                aria-label={show ? "Hide password" : "Show password"}
                aria-pressed={show}
              >
                <Svg d={show ? EYE_OFF : EYE} strokeWidth="1.5" />
              </button>
            </div>
            <ul className={styles.rules} id="welcome-password-rules">
              {RULES.map(([label, test]) => (
                <li key={label} className={test(password) ? styles.ok : ""}>
                  {label}
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.field}>
            <span className={styles.label}>
              Phone number
            </span>
            <div className={styles.phone}>
              <PhoneInputWithCountryCode setPhone={setPhone} label="Phone" />
            </div>
            <p className={styles.help}>We’ll message you on WhatsApp to help you get started.</p>
          </div>

          {!ready && !busy && (password || phone) && (
            <p className={styles.help} role="status">
              {!valid ? "Your password still misses one of the checks above." : "Enter a valid phone number to continue."}
            </p>
          )}
          <button type="submit" className={styles.primary} disabled={!ready || busy}>
            {busy ? "Creating account…" : "Create account"}
          </button>
          <p className={styles.terms}>
            By creating an account you agree to our{" "}
            <a href="/help/terms-and-conditions" target="_blank" rel="noreferrer">
              Terms and conditions
            </a>{" "}
            and{" "}
            <a href="/help/privacy" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </form>
    </Modal>
  );
}
